import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import usePresence from '../../hooks/usePresence';
import Avatar from '../common/Avatar';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Textarea } from '../ui/textarea';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '../ui/sheet';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { 
  MessageSquare,
  Send,
  Paperclip,
  Image,
  FileText,
  Download,
  Clock,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Loader2,
  MoreVertical,
  Play,
  ThumbsUp,
  ThumbsDown,
  ExternalLink,
  Upload,
  DollarSign,
  Users,
  Calendar
} from 'lucide-react';
import { cn } from '../../lib/utils';
import api from '../../lib/axios';
import { toast } from 'sonner';

const ProjectChat = ({ projectId, projectName, isOpen, onOpenChange }) => {
  const { user, isClient } = useAuth();
  const { theme } = useTheme();
  const { onlineCount } = usePresence(projectId, user?.id);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [messageType, setMessageType] = useState('text');
  const [actionType, setActionType] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    if (isOpen && projectId) {
      fetchMessages();
      const interval = setInterval(fetchMessages, 10000);
      return () => clearInterval(interval);
    }
  }, [isOpen, projectId]);

  useEffect(() => {
    scrollToBottom();
    // Mark messages as read when viewing
    if (isOpen && messages.length > 0) {
      markMessagesAsRead();
    }
  }, [messages, isOpen]);

  const markMessagesAsRead = async () => {
    try {
      // Find unread messages not sent by current user
      const unreadMessageIds = messages
        .filter(msg => msg.sender_id !== user?.id && !msg.read_at && !msg.read_by?.includes(user?.id))
        .map(msg => msg.id);
      
      if (unreadMessageIds.length > 0) {
        await api.post(`/projects/${projectId}/messages/mark-read`, {
          message_ids: unreadMessageIds
        });
      }
    } catch (error) {
      console.error('Error marking messages as read:', error);
    }
  };

  const fetchMessages = async () => {
    try {
      const res = await api.get(`/projects/${projectId}/messages`);
      let msgList = res.data || [];
      
      // Filter out action_request messages for clients (BAT validation, budget extension, etc.)
      if (isClient) {
        msgList = msgList.filter(msg => msg.message_type !== 'action_request');
      }
      
      setMessages(msgList);
    } catch (error) {
      console.error('Error fetching messages:', error);
    } finally {
      setLoading(false);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSend = async () => {
    if (!newMessage.trim() && messageType === 'text' && !selectedFile) return;

    setSending(true);
    try {
      let attachmentUrl = null;
      
      // Upload file if selected
      if (selectedFile) {
        setUploading(true);
        const formData = new FormData();
        formData.append('file', selectedFile);
        formData.append('type', 'chat');
        
        try {
          const uploadRes = await api.post('/upload', formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
          attachmentUrl = uploadRes.data.url;
        } catch (uploadError) {
          toast.error('Erreur lors de l\'upload du fichier');
          setUploading(false);
          setSending(false);
          return;
        }
        setUploading(false);
      }

      const payload = {
        content: newMessage || (selectedFile ? `Fichier: ${selectedFile.name}` : ''),
        message_type: selectedFile ? 'file' : messageType,
        attachment_url: attachmentUrl
      };

      if (actionType) {
        payload.action_type = actionType;
      }

      await api.post(`/projects/${projectId}/messages`, payload);
      setNewMessage('');
      setMessageType('text');
      setActionType(null);
      setSelectedFile(null);
      await fetchMessages();
    } catch (error) {
      toast.error('Erreur lors de l\'envoi du message');
    } finally {
      setSending(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      setMessageType('file');
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer?.files?.[0];
    if (file) {
      setSelectedFile(file);
      setMessageType('file');
    }
  };

  const handleActionResponse = async (messageId, response) => {
    try {
      await api.put(`/projects/${projectId}/messages/${messageId}/action`, {
        action_response: response
      });
      await fetchMessages();
      toast.success(response === 'approved' ? 'Demande approuvée' : 'Demande refusée');
    } catch (error) {
      toast.error('Erreur lors de la réponse');
    }
  };

  const formatTime = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now - date;
    
    if (diff < 60000) return "À l'instant";
    if (diff < 3600000) return `Il y a ${Math.floor(diff / 60000)} min`;
    if (diff < 86400000) return date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
    return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
  };

  const getMessageIcon = (type) => {
    switch (type) {
      case 'file': return <FileText className="w-4 h-4" />;
      case 'image': return <Image className="w-4 h-4" />;
      case 'action_request': return <AlertTriangle className="w-4 h-4" />;
      default: return null;
    }
  };

  const getActionIcon = (actionType) => {
    switch (actionType) {
      case 'bat_validation': return <CheckCircle2 className="w-4 h-4" />;
      case 'budget_extension': return <Clock className="w-4 h-4" />;
      case 'deadline_change': return <Clock className="w-4 h-4" />;
      default: return <AlertTriangle className="w-4 h-4" />;
    }
  };

  const getActionLabel = (actionType) => {
    switch (actionType) {
      case 'bat_validation': return 'Validation BAT';
      case 'budget_extension': return 'Extension budget';
      case 'deadline_extension': return 'Extension délai';
      case 'deadline_change': return 'Modification délai';
      default: return 'Demande';
    }
  };

  const renderMessage = (msg) => {
    const isOwn = msg.sender_id === user?.id;
    const isAction = msg.message_type === 'action_request';
    const isRead = msg.read_at || msg.read_by?.length > 0;
    
    return (
      <div
        key={msg.id}
        className={cn(
          "flex gap-3 mb-4",
          isOwn ? "flex-row-reverse" : ""
        )}
      >
        <Avatar 
          name={msg.sender_name || 'Utilisateur'} 
          size="sm" 
          type={msg.sender_role === 'agency' ? 'agency' : msg.sender_role === 'client' ? 'client' : 'creative'}
        />
        
        <div className={cn("max-w-[75%]", isOwn && "text-right")}>
          {/* Sender info */}
          <div className={cn("flex items-center gap-2 mb-1", isOwn && "flex-row-reverse")}>
            <span className="text-xs font-medium text-[#1a365d]">
              {msg.sender_name}
            </span>
            <span className="text-xs text-[#94a3b8]">
              {formatTime(msg.created_at)}
            </span>
            {/* Read indicator for own messages */}
            {isOwn && (
              <span className={cn("text-xs flex items-center gap-1", isRead ? "text-emerald-500" : "text-[#94a3b8]")}>
                {isRead ? (
                  <>
                    <CheckCircle2 className="w-3 h-3" />
                    <span className="text-[10px]">
                      {msg.read_by && msg.read_by.length > 1 
                        ? `Lu par ${msg.read_by.length - 1}` 
                        : 'Lu'}
                    </span>
                  </>
                ) : (
                  <>
                    <Clock className="w-3 h-3" />
                    <span className="text-[10px]">Envoyé</span>
                  </>
                )}
              </span>
            )}
          </div>
          
          {/* Message bubble */}
          <div
            className={cn(
              "rounded-2xl px-4 py-2.5",
              isOwn 
                ? "bg-[var(--color-accent)] text-white rounded-tr-md" 
                : "bg-[#f1f5f9] text-[#1a365d] rounded-tl-md",
              isAction && "border-2 border-amber-400 bg-amber-50"
            )}
          >
            {/* Action request header */}
            {isAction && (
              <div className="flex items-center gap-2 mb-2 pb-2 border-b border-amber-200">
                {getActionIcon(msg.action_type)}
                <span className="font-semibold text-amber-700">
                  {getActionLabel(msg.action_type)}
                </span>
                {msg.action_response && (
                  <Badge 
                    className={cn(
                      "ml-auto",
                      msg.action_response === 'approved' ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"
                    )}
                  >
                    {msg.action_response === 'approved' ? 'Approuvé' : 'Refusé'}
                  </Badge>
                )}
              </div>
            )}
            
            {/* Message content */}
            <p className={cn("text-sm whitespace-pre-wrap", isAction && "text-amber-900")}>
              {msg.content}
            </p>
            
            {/* File attachment */}
            {msg.file_url && (
              <a 
                href={msg.file_url} 
                target="_blank" 
                rel="noopener noreferrer"
                className={cn(
                  "flex items-center gap-2 mt-2 text-xs underline",
                  isOwn ? "text-white/80" : "text-[var(--color-accent)]"
                )}
              >
                <Download className="w-3 h-3" />
                Télécharger le fichier
              </a>
            )}
            
            {/* Action buttons for pending requests */}
            {isAction && !msg.action_response && msg.sender_id !== user?.id && (
              <div className="flex gap-2 mt-3 pt-2 border-t border-amber-200">
                <Button 
                  size="sm" 
                  variant="outline"
                  className="flex-1 border-green-500 text-green-600 hover:bg-green-50"
                  onClick={() => handleActionResponse(msg.id, 'approved')}
                >
                  <ThumbsUp className="w-4 h-4 mr-1" />
                  Approuver
                </Button>
                <Button 
                  size="sm" 
                  variant="outline"
                  className="flex-1 border-red-500 text-red-600 hover:bg-red-50"
                  onClick={() => handleActionResponse(msg.id, 'rejected')}
                >
                  <ThumbsDown className="w-4 h-4 mr-1" />
                  Refuser
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const actionTypes = [
    { id: 'bat_validation', label: 'Demande validation BAT', icon: CheckCircle2, description: 'Soumettre un BAT pour approbation client' },
    { id: 'deadline_extension', label: 'Extension de délai', icon: Calendar, description: 'Demander plus de temps' },
    { id: 'budget_extension', label: 'Extension budget', icon: DollarSign, description: 'Demander un budget supplémentaire' },
  ];

  return (
    <Sheet open={isOpen} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-lg flex flex-col p-0">
        <SheetHeader className="p-4 border-b">
          <SheetTitle className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <MessageSquare className="w-5 h-5" style={{ color: theme.colors?.accent }} />
              <span>Chat - {projectName}</span>
            </div>
            {/* Online indicator with real count */}
            <div className="flex items-center gap-1.5 text-xs">
              <span className={cn(
                "w-2 h-2 rounded-full",
                onlineCount > 0 ? "bg-emerald-500 animate-pulse" : "bg-slate-300"
              )} />
              <span className={cn(
                "font-medium",
                onlineCount > 0 ? "text-emerald-600" : "text-slate-400"
              )}>
                {onlineCount > 0 ? (
                  <>
                    <Users className="w-3 h-3 inline mr-1" />
                    {onlineCount} en ligne
                  </>
                ) : (
                  'Hors ligne'
                )}
              </span>
            </div>
          </SheetTitle>
        </SheetHeader>

        {/* Messages container */}
        <div className="flex-1 overflow-y-auto p-4 space-y-1">
          {loading ? (
            <div className="flex items-center justify-center h-full">
              <Loader2 className="w-6 h-6 animate-spin text-[#64748b]" />
            </div>
          ) : messages.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center">
              <MessageSquare className="w-12 h-12 text-[#e2e8f0] mb-3" />
              <p className="text-[#64748b]">Aucun message</p>
              <p className="text-sm text-[#94a3b8]">Commencez la conversation !</p>
            </div>
          ) : (
            <>
              {messages.map(renderMessage)}
              <div ref={messagesEndRef} />
            </>
          )}
        </div>

        {/* Selected file indicator */}
        {selectedFile && (
          <div className="px-4 py-2 bg-blue-50 border-t border-blue-200 flex items-center justify-between">
            <div className="flex items-center gap-2 text-sm text-blue-700">
              <FileText className="w-4 h-4" />
              <span className="truncate max-w-[200px]">{selectedFile.name}</span>
              <span className="text-xs text-blue-500">({(selectedFile.size / 1024).toFixed(1)} KB)</span>
            </div>
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => { setSelectedFile(null); setMessageType('text'); }}
            >
              <XCircle className="w-4 h-4" />
            </Button>
          </div>
        )}

        {/* Action type indicator */}
        {messageType === 'action_request' && actionType && (
          <div className="px-4 py-2 bg-amber-50 border-t border-amber-200 flex items-center justify-between">
            <div className="flex items-center gap-2 text-sm text-amber-700">
              <AlertTriangle className="w-4 h-4" />
              <span>Demande : {getActionLabel(actionType)}</span>
            </div>
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => { setMessageType('text'); setActionType(null); }}
            >
              <XCircle className="w-4 h-4" />
            </Button>
          </div>
        )}

        {/* Hidden file input */}
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileSelect}
          className="hidden"
          accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.zip"
        />

        {/* Input area */}
        <div 
          className="p-4 border-t bg-white"
          onDrop={handleDrop}
          onDragOver={(e) => e.preventDefault()}
        >
          <div className="flex items-end gap-2">
            {/* Attachment & Action buttons */}
            <div className="flex gap-1">
              <Button 
                variant="ghost" 
                size="icon" 
                className={cn("h-9 w-9", selectedFile && "bg-blue-100")}
                onClick={() => fileInputRef.current?.click()}
                title="Joindre un fichier"
              >
                <Paperclip className="w-4 h-4" />
              </Button>

              {/* Action requests - Agency/Team only, not for clients */}
              {!isClient && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      className={cn("h-9 w-9", messageType === 'action_request' && "bg-amber-100")}
                    >
                      <AlertTriangle className="w-4 h-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start">
                    {actionTypes.map((action) => (
                      <DropdownMenuItem 
                        key={action.id}
                        onClick={() => { setMessageType('action_request'); setActionType(action.id); }}
                      >
                        <action.icon className="w-4 h-4 mr-2" />
                        {action.label}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>

            {/* Message input */}
            <Textarea
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              placeholder={
                messageType === 'action_request' 
                  ? "Décrivez votre demande..." 
                  : "Votre message..."
              }
              className="flex-1 min-h-[40px] max-h-[120px] resize-none"
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSend();
                }
              }}
            />

            {/* Send button */}
            <Button 
              onClick={handleSend}
              disabled={sending || uploading || (!newMessage.trim() && messageType === 'text' && !selectedFile)}
              size="icon"
              className="h-10 w-10 shrink-0"
              style={{ backgroundColor: theme.colors?.accent }}
            >
              {sending || uploading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Send className="w-4 h-4" />
              )}
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default ProjectChat;
